import React, { useEffect, useState } from 'react';
import AdminTable from 'common/AdminTable';
import { toast } from 'react-toastify';
import axiosInstance from 'apis/axiosConfig';

const ManageCategories = () => {
    const [data, setData] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentCategory, setCurrentCategory] = useState({
        id: null,
        name: '',
        description: ''
    });

    const fetchData = async () => {
        try {
            const res = await axiosInstance.get('loan-categories');
            setData(res.data);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load categories");
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleAddClick = () => {
        setIsEdit(false);
        setCurrentCategory({ id: null, name: '', description: '' });
        setShowModal(true);
    };

    const handleEditClick = (category) => {
        setIsEdit(true);
        setCurrentCategory({
            id: category.id,
            name: category.name,
            description: category.description || ''
        });
        setShowModal(true);
    };

    const handleToggleStatus = async (category) => {
        try {
            await axiosInstance.delete(`loan-categories/${category.id}`);
            const newStatus = category.is_active === 1 ? 'Inactive' : 'Active';
            toast.success(`Category is now ${newStatus}!`);
            fetchData();
        } catch (err) {
            console.error(err);
            toast.error("Failed to update status");
        }
    };

    const handleSave = async () => {
        if (!currentCategory.name.trim()) {
            toast.warning("Category Name is required!");
            return;
        }

        try {
            if (isEdit) {
                await axiosInstance.put(`loan-categories/${currentCategory.id}`, {
                    name: currentCategory.name,
                    description: currentCategory.description
                });
                toast.success("Category Updated successfully!");
            } else {
                await axiosInstance.post('loan-categories', {
                    name: currentCategory.name,
                    description: currentCategory.description
                });
                toast.success("Category Created successfully!");
            }
            setShowModal(false);
            fetchData();
        } catch (err) {
            console.error(err);
            const errMsg = err.response?.data?.message || "Failed to save category";
            toast.error(errMsg);
        }
    };

    const tableHeaders = [
        { Header: 'ID', accessor: 'id' },
        { Header: 'Category Name', accessor: 'name', Cell: ({ row }) => <strong className="text-primary">{row.original.name}</strong> },
        { Header: 'Description', accessor: 'description', Cell: ({ row }) => row.original.description || '--' },
        { 
            Header: 'Status', 
            accessor: 'is_active', 
            Cell: ({ row }) => (
                <span className={`badge ${row.original.is_active === 1 ? 'badge-success' : 'badge-secondary'}`}>
                    {row.original.is_active === 1 ? 'ACTIVE' : 'INACTIVE'}
                </span>
            ) 
        },
        { 
            Header: 'Action', 
            accessor: 'action', 
            Cell: ({ row }) => (
                <div className="d-flex gap-2">
                    <button 
                        className="btn btn-warning btn-sm text-dark font-weight-bold mr-2" 
                        onClick={() => handleEditClick(row.original)}
                    >
                        <i className="fa fa-edit"></i> Edit
                    </button>
                    <button 
                        className={`btn btn-sm font-weight-bold text-white ${row.original.is_active === 1 ? 'btn-danger' : 'btn-success'}`} 
                        onClick={() => handleToggleStatus(row.original)}
                    >
                        <i className={`fa ${row.original.is_active === 1 ? 'fa-toggle-off' : 'fa-toggle-on'}`}></i> {row.original.is_active === 1 ? 'Deactivate' : 'Activate'}
                    </button>
                </div>
            ) 
        }
    ];

    return (
        <div className='container-fluid mt-5'>
            <div className='mainheadig mx-4 d-flex justify-content-between align-items-center mb-4'>
                <h4 className='text-white font-weight-bold mb-0'>Manage Categories</h4>
                <button className="btn btn-info font-weight-bold px-4 py-2 shadow-sm" onClick={handleAddClick}>
                    <i className="fa fa-plus-circle mr-2"></i> Add Category
                </button>
            </div>
            
            {data?.length > 0 ? (
                <AdminTable tableHeaders={tableHeaders} tableData={data} />
            ) : (
                <div className="mx-4 card p-4 text-center shadow-sm">
                    <p className="text-muted mb-0 font-weight-bold">No Categories Found. Click Add Category to create one!</p>
                </div>
            )}

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content shadow-lg border-0">
                            <div className="modal-header bg-info text-white">
                                <h5 className="modal-title font-weight-bold">{isEdit ? 'Update Category' : 'Create New Category'}</h5>
                                <button type="button" className="close text-white" onClick={() => setShowModal(false)}>&times;</button>
                            </div>
                            <div className="modal-body p-4">
                                <div className="form-group mb-3">
                                    <label htmlFor="catName" className="font-weight-bold text-dark">Category Name:</label>
                                    <input 
                                        id="catName" 
                                        type="text" 
                                        className="form-control mt-1" 
                                        placeholder="e.g. Home Loan, Business Loan"
                                        value={currentCategory.name} 
                                        onChange={(e) => setCurrentCategory({...currentCategory, name: e.target.value})} 
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="catDesc" className="font-weight-bold text-dark">Description (Optional):</label>
                                    <textarea 
                                        id="catDesc" 
                                        className="form-control mt-1" 
                                        rows="3"
                                        placeholder="Brief description of the loan category"
                                        value={currentCategory.description} 
                                        onChange={(e) => setCurrentCategory({...currentCategory, description: e.target.value})} 
                                    />
                                </div>
                            </div>
                            <div className="modal-footer bg-light">
                                <button type="button" className="btn btn-secondary font-weight-bold" onClick={() => setShowModal(false)}>Close</button>
                                <button type="button" className="btn btn-info font-weight-bold px-4" onClick={handleSave}>{isEdit ? 'Save Changes' : 'Create Category'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageCategories;
